const STORAGE_KEY_PREFIX = "terminal-polygon:";
/** Skip sessionStorage for large GeoJSON — quota / main-thread cost. Memory cache still holds them. */
const MAX_SESSION_STORAGE_CHARS = 40_000;

const memoryCache = new Map();

function isSessionStorageAvailable() {
  try {
    const testKey = "__terminal_polygon_cache_test__";
    sessionStorage.setItem(testKey, "1");
    sessionStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

function getStorageKey(terminalID, modelVersionInt) {
  return `${STORAGE_KEY_PREFIX}${terminalID}:${modelVersionInt}`;
}

function getMemoryKey(terminalID, modelVersionInt) {
  return `${terminalID}:${modelVersionInt}`;
}

function deserialize(raw) {
  const parsed = JSON.parse(raw);
  if (!Object.prototype.hasOwnProperty.call(parsed, "v")) {
    throw new Error("Invalid terminal polygon cache entry");
  }
  return parsed.v;
}

export function getCachedTerminalGeoJSON(terminalID, modelVersionInt) {
  const memoryKey = getMemoryKey(terminalID, modelVersionInt);
  if (memoryCache.has(memoryKey)) {
    return memoryCache.get(memoryKey);
  }

  if (!isSessionStorageAvailable()) {
    return undefined;
  }

  const raw = sessionStorage.getItem(getStorageKey(terminalID, modelVersionInt));
  if (raw === null) {
    return undefined;
  }

  try {
    const terminalGeoJSON = deserialize(raw);
    memoryCache.set(memoryKey, terminalGeoJSON);
    return terminalGeoJSON;
  } catch (error) {
    console.warn("Terminal polygon cache: invalid sessionStorage entry removed", error);
    sessionStorage.removeItem(getStorageKey(terminalID, modelVersionInt));
    return undefined;
  }
}

export function setCachedTerminalGeoJSON(terminalID, modelVersionInt, terminalGeoJSON) {
  const memoryKey = getMemoryKey(terminalID, modelVersionInt);
  memoryCache.set(memoryKey, terminalGeoJSON);

  if (terminalGeoJSON == null || !isSessionStorageAvailable()) {
    return;
  }

  try {
    const payload = JSON.stringify({ v: terminalGeoJSON });
    if (payload.length > MAX_SESSION_STORAGE_CHARS) {
      return;
    }
    sessionStorage.setItem(getStorageKey(terminalID, modelVersionInt), payload);
  } catch (error) {
    console.warn("Terminal polygon cache: sessionStorage write failed", error);
  }
}

/**
 * Seed cache from get_data_for_filtering rows (modelVersionInt 0).
 * Lets dashboard / map reuse polygons without per-terminal fetches.
 */
export function seedTerminalPolygonCacheFromFilterData(rows, modelVersionInt = 0) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return { seeded: 0, skipped: 0 };
  }

  let seeded = 0;
  let skipped = 0;

  rows.forEach((row) => {
    const terminalID = row?.terminalID;
    if (terminalID == null) {
      skipped += 1;
      return;
    }
    const terminalGeoJSON = row.terminalGeoJSON ?? null;
    if (!terminalGeoJSON) {
      skipped += 1;
      return;
    }
    setCachedTerminalGeoJSON(terminalID, modelVersionInt, terminalGeoJSON);
    seeded += 1;
  });

  return { seeded, skipped };
}

/**
 * Attach terminalGeoJSON from filter rows onto dashboard markers missing polygons.
 * Prefer this over network fetch when filter payload already includes geometry.
 */
export function mergeFilterPolygonsOntoMarkers(markers, filterRows) {
  if (!Array.isArray(markers) || markers.length === 0) {
    return { markers: markers ?? [], merged: 0 };
  }
  if (!Array.isArray(filterRows) || filterRows.length === 0) {
    return { markers, merged: 0 };
  }

  const geoByTerminalId = new Map();
  filterRows.forEach((row) => {
    if (row?.terminalID == null || !row.terminalGeoJSON) return;
    geoByTerminalId.set(row.terminalID, row.terminalGeoJSON);
  });

  let merged = 0;
  const next = markers.map((marker) => {
    if (marker?.terminalGeoJSON) return marker;
    const terminalGeoJSON = geoByTerminalId.get(marker.terminalID);
    if (!terminalGeoJSON) return marker;
    merged += 1;
    return { ...marker, terminalGeoJSON };
  });

  return { markers: next, merged };
}
